import { Box, Button, IconButton } from "@origin-digital/ods-core";
import { IconPlus } from "@origin-digital/ods-icons";
import React, { useEffect, useState } from "react";
import AddContact from "./AddContact";
import { Field, useFormikContext } from "formik";
import AutocompleteField from "./AutocompleteField";
import { DraftIcon } from "src/components/Projects/Icons";
import { Contact, FormValues } from "../types";
import { apiClient } from "src/api/apiService";
import { getNestedValue, removeFieldImmutable } from "../helper";
import { ChangeRequestFormValues } from "../ChangeRequest/formValue";
import { useResponsiveBelowSm } from "src/hooks";
import { camelCase } from "lodash";

interface IContactFieldProps {
  name:
    | "licensedElectrician"
    | "asp"
    | "siteSupervisor"
    | "gasLine.licensedPlumber"
    | "both.licensedPlumber"
    | "gasMeter.licensedPlumber"
    | "internetPreInstaller"
    | "internetSiteSupervisor";
  contactType:
    | "ELECTRICIAN"
    | "SERVICE_PROVIDER"
    | "SUPERVISOR"
    | "PLUMBER"
    | "INTERNET_PREINSTALLER";
  label: string;
  validatePlumberLicense?: (license: string) => Promise<{
    valid: boolean;
    message: string;
  }>;
}

const ContactField: React.FC<IContactFieldProps> = ({
  contactType,
  name,
  label,
  validatePlumberLicense,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { values, setFieldValue, setFieldError, errors, setErrors } =
    useFormikContext<FormValues | ChangeRequestFormValues>();
  const msaNumber = values.requestingCompany?.msaNumber;

  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const contactFieldValue = getNestedValue(values, name) as string;
  const isBelowSm = useResponsiveBelowSm();

  const closeDialog = () => {
    setOpen(false);
  };
  const openDialog = () => {
    setOpen(true);
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      // Fetch contacts
      const response = await apiClient.get<Contact[]>(`/contact/${msaNumber}`, {
        params: { type: contactType },
      });
      // siteSupervisor is a special case to handle the sync between multiple usages in the form
      if (name === "siteSupervisor") {
        setFieldValue("siteSupervisors", response.data);
      }
      // siteSupervisor is a special case to handle the sync between multiple usages in the form
      if (name === "internetSiteSupervisor") {
        setFieldValue("internetSiteSupervisors", response.data);
      }

      // licensedPlumbers needs to be recorded for validation in submission
      if (
        [
          "gasLine.licensedPlumber",
          "both.licensedPlumber",
          "gasMeter.licensedPlumber",
        ].includes(name)
      ) {
        setFieldValue("licensedPlumbers", response.data);
      } else {
        // licensedElectrician needs to be recorded for change request
        setFieldValue("licensedElectricians", response.data);
        setContacts(response.data);
      }
      setLoading(false);
    } catch (error) {
      console.log(`Fetch contacts error: ${error}`);
    }
  };

  const getContactList = () => {
    if (name === "siteSupervisor") {
      return (values as FormValues).siteSupervisors;
    }
    if (
      [
        "gasLine.licensedPlumber",
        "both.licensedPlumber",
        "gasMeter.licensedPlumber",
      ].includes(name)
    ) {
      return (values as FormValues).licensedPlumbers;
    }
    return contacts;
  };

  const contactList = getContactList();

  useEffect(() => {
    if (msaNumber) {
      fetchContacts();
    }
  }, [msaNumber, contactType]);

  useEffect(() => {
    const setValidationErrors = async () => {
      if (
        [
          "gasLine.licensedPlumber",
          "both.licensedPlumber",
          "gasMeter.licensedPlumber",
        ].includes(name) &&
        contactFieldValue &&
        (values as FormValues).premiseAddress?.state === "VIC" &&
        validatePlumberLicense
      ) {
        const licenseNumber = contactList.find(
          (c) => c.id === contactFieldValue
        )?.license_number;
        if (licenseNumber) {
          const { message, valid } = await validatePlumberLicense(
            licenseNumber
          );
          if (valid) {
            setErrors(removeFieldImmutable(errors, name) ?? {});
          } else {
            setFieldError(name, message);
          }
        }
      }
    };
    setValidationErrors();
  }, [contactFieldValue, name]);

  return (
    <>
      <Box display="flex" alignItems="flex-start" justifyContent="flex-start">
        <Field
          id={`${name}-autocomplete-field`}
          component={AutocompleteField}
          name={name}
          label={label}
          options={contactList.map((contact: Contact) => ({
            label: `${contact.first_name} ${contact.last_name}`,
            value: contact.id,
          }))}
          loading={loading}
        />
        {/* Add or Edit button */}
        <Box marginTop="xxsmall">
          {isBelowSm ? (
            <IconButton onClick={openDialog}>
              {contactFieldValue ? (
                <DraftIcon data-id={camelCase(`${name} edit-contact-button`)} />
              ) : (
                <IconPlus
                  color="critical"
                  data-id={camelCase(`${name} add-contact-button`)}
                />
              )}
            </IconButton>
          ) : (
            <>
              {contactFieldValue ? (
                <Button
                  variant="text"
                  icon={<DraftIcon color="inherit" />}
                  onClick={openDialog}
                  data-id={camelCase(`${name} edit-contact-button`)}
                >
                  Edit
                </Button>
              ) : (
                <Button
                  variant="text"
                  icon={<IconPlus color="inherit" />}
                  onClick={openDialog}
                  data-id={camelCase(`${name} add-contact-button`)}
                >
                  Add
                </Button>
              )}
            </>
          )}
        </Box>
      </Box>
      {open && (
        <AddContact
          closeDialog={closeDialog}
          selectedContact={contactList.find(
            (e) => e.id === getNestedValue(values, name)
          )}
          fetchContacts={fetchContacts}
          setFieldValue={setFieldValue}
          name={name}
          label={label.toLowerCase()}
          contactType={contactType}
          msaNumber={msaNumber}
          state={
            "premiseAddress" in values
              ? values.premiseAddress?.state
              : values.premise?.site_details.state ?? ""
          }
        />
      )}
    </>
  );
};
export default ContactField;
